<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/apiService';
import ArgonAlert from "@/components/ArgonAlert.vue";
import CategoryBlogModal from './components/CategoryBlogModal.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

const route = useRoute();
const router = useRouter();
const imgUrl = process.env.VUE_APP_API;

const isEditMode = ref(false);
const title = ref('');
const content = ref('');
const selectedCategory = ref('');
const categories = ref([]);
const imageFile = ref(null);
const imagePreview = ref('');
const loading = ref(true);
const publicationDate = ref('');
const showCategoryModal = ref(false);
const showModal = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB

const fetchBlog = async () => {
  try {
    const response = await api.get(`/blog_posts/${route.params.id}`);
    const data = response.data;

    title.value = data.title;
    content.value = data.content;
    selectedCategory.value = String(data.category?.id || '');
    publicationDate.value = data.publicationDate || '';
    imagePreview.value = data.imageName
      ? `${imgUrl}/uploads/images/${data.imageName}`
      : '';
  } catch (err) {
    errorMessage.value = 'Error fetching blog post.';
  } finally {
    loading.value = false;
  }
};

const fetchCategories = async () => {
  try {
    const response = await api.get('/blog_categories?pagination=false');
    categories.value = response.data.member || [];
  } catch (err) {
    console.error('Failed to load categories');
  }
};

const handleCategorySaved = (newCategory) => {
  if (!newCategory?.id) return;
  categories.value.push(newCategory);
  selectedCategory.value = newCategory.id;
};

const handleImageUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    if (file.size > MAX_IMAGE_SIZE) {
      errorMessage.value = 'La imagen es demasiado grande. El tamaño máximo permitido es 2MB.';
      imageFile.value = null;
      imagePreview.value = '';
    } else {
      imageFile.value = file;
      imagePreview.value = URL.createObjectURL(file);
      errorMessage.value = '';
    }
  }
};


const savePost = async () => {
  // Reset messages
  errorMessage.value = '';
  successMessage.value = '';

  // Check required fields
  if (!title.value || !content.value || !selectedCategory.value) {
    errorMessage.value = 'Título, contenido y categoría son obligatorios.';
    return;
  }

  // Validate image size again before upload
  if (imageFile.value && imageFile.value.size > MAX_IMAGE_SIZE) {
    errorMessage.value = 'La imagen es demasiado grande para subirla. Máximo permitido: 2MB.';
    return;
  }

  const formData = new FormData();
  formData.append('title', title.value);
  formData.append('content', content.value);
  formData.append('category', selectedCategory.value);
  formData.append('publicationDate', publicationDate.value || new Date().toISOString());

  if (imageFile.value) {
    formData.append('imageFile', imageFile.value);
  }

  const author = JSON.parse(localStorage.getItem('userData'));
  if (author?.id) {
    formData.append('author', author.id);
  } else {
    errorMessage.value = 'Autor no encontrado. Inicia sesión nuevamente.';
    return;
  }

  try {
    if (isEditMode.value) {
      formData.append('_method', 'PUT');
      await api.post(`/blog-posts/${route.params.id}`, formData);
      successMessage.value = '¡Blog actualizado con éxito!';
    } else {
      await api.post('/blog-posts', formData);
      successMessage.value = '¡Blog creado con éxito!';
    }

    // Redirect after success
    setTimeout(() => {
      router.push('/blogs');
    }, 1500);
  } catch (err) {
    if (err.response?.status === 413) {
      errorMessage.value = 'La imagen subida es demasiado grande. Intenta con una más pequeña.';
    } else if (err.message === 'Network Error') {
      errorMessage.value = 'La imagen subida es demasiado grande. Intenta con una más pequeña.';
    } else {
      errorMessage.value = `Error ${isEditMode.value ? 'actualizando' : 'creando'} el blog.`;
    }
  }
};


onMounted(() => {
  isEditMode.value = !!route.params.id;
  fetchCategories();
  if (isEditMode.value) {
    fetchBlog();
  } else {
    publicationDate.value = new Date().toISOString().split('T')[0];
    loading.value = false;
  }
});
</script>

<template>
  <div class="container py-4" v-if="!loading">
    <div class="row">
      <!-- Columna izquierda: Editor -->
      <div class="col-md-9">
        <argon-alert v-if="successMessage" color="alert-success" icon="ni ni-check-bold"
          class="mb-2 text-dark w-50 ms-3">
          {{ successMessage }}
        </argon-alert>

        <argon-alert v-if="errorMessage && !showModal" color="alert-danger" icon="ni ni-fat-remove"
          class="mb-2 text-dark w-50 ms-3">
          {{ errorMessage }}
        </argon-alert>

        <input v-model="title" type="text" class="form-control form-control-lg mb-3" placeholder="Título del blog" />

        <QuillEditor v-model:content="content" content-type="html" style="height: 300px; color: #495057;
            background-color: #fff;
            background-clip: padding-box;
            border: 1px solid #d2d6da;
           " theme="snow" toolbar="Essential"
         />
      </div>

      <!-- Columna derecha: Barra lateral -->
      <div class="col-md-3">
        <!-- Caja de publicación -->
        <div class="card mb-3 shadow-sm">
          <div class="card-body">
            <h6 class="card-title">Publicar</h6>

            <button class="btn btn-secondary btn-sm me-2" @click="router.push('/blog')">
              Cancelar
            </button>
            <button @click="savePost" class="btn btn-primary btn-sm">
              {{ isEditMode ? 'Guardar' : 'Crear' }}
            </button>
          </div>
        </div>

        <!-- Categorías -->
        <div class="card mb-3 shadow-sm">
          <div class="card-body">
            <h6 class="card-title">Categorías</h6>
            <select v-model="selectedCategory" class="form-select mb-2">
              <option value="">-- Selecciona una categoría --</option>
              <option v-for="cat in categories" :key="cat.id" :value="String(cat.id)">
                {{ cat.name }}
              </option>
            </select>
            <a href="#" class="text-primary text-sm" @click.prevent="showCategoryModal = true">
              + Añadir nueva categoría
            </a>
          </div>
        </div>

        <!-- Subida de imagen -->
        <div class="card mb-3 shadow-sm">
          <div class="card-body">
            <h6 class="card-title">Imagen destacada</h6>
            <div class="mb-2">
              <img v-if="imagePreview" :src="imagePreview" alt="Vista previa" class="img-fluid rounded" />
              <div v-else class="text-muted small">Ninguna imagen seleccionada</div>
              <div v-if="imageFile" class="text-muted small mt-1">
                Tamaño: {{ (imageFile.size / 1024 / 1024).toFixed(2) }} MB
              </div>
            </div>
            <input type="file" @change="handleImageUpload" class="form-control" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de categoría -->
    <CategoryBlogModal :show="showCategoryModal" @close="showCategoryModal = false" @saved="handleCategorySaved" />
  </div>

  <div v-else class="text-center py-5 text-muted">Cargando blog...</div>
</template>

<style scoped>
.card-title {
  font-weight: 600;
  font-size: 1rem;
}
</style>
